/* eslint-disable arrow-body-style */
/* eslint-disable dot-notation */
// Need assumed values here. This is a report, not a production app.

import * as React from 'react';
import { FC, useEffect, useRef } from 'react';
import { Card, Col, Container, Row, Table } from 'react-bootstrap';
import apiCalls from '../PowerShadesAPIFunctions';

type VauluesExpected = {
	SalesOrderAmountOrdered: number;
	InvoiceOrdered: number;
	ProjectedCostOrdered: number;
	ProjectedInvoiceOrdered: number;
	SalesOrderAmountShipped: number;
	InvoiceShipped: number;
	ProjectedCostShipped: number;
	ProjectedInvoiceShipped: number;
	ShadesShipped: number;
	ShadesOrdered: number;
}

type DateRange = {
	MonthValue: number;
	YearValue: number;
	Begin: Date;
	End: Date;
	Data: "Loading" | VauluesExpected | null;
};

type TopViewState = {
	dateRanges: DateRange[];
}

const TopView: FC = () => {
	// const isPsAdmin = useIsPowerShadesAdmin();

	const dataValsRef = React.useRef<TopViewState>({
		dateRanges: [],
	});

	const isSpringsStyleRef = React.useRef(false);
	const isSpringsStyle = isSpringsStyleRef.current;
	const [isLoading, setIsLoading] = React.useState(true);

	const toggleStyle = () => {
		isSpringsStyleRef.current = !isSpringsStyleRef.current;
		dataValsRef.current = { dateRanges: [] };
		setIsLoading(!isLoading);
		countRef.current = 0;
	};

	useEffect(() => {
		generateData(isSpringsStyleRef.current);
		countRef.current = 0;
	}, [isSpringsStyleRef.current]);

	const dataVals = dataValsRef.current;


	const setDataVals = (newData: TopViewState, isSpringsStyle: boolean) => {
		if (isSpringsStyle !== isSpringsStyleRef.current) return;
		dataValsRef.current = newData;
	};


	const countRef = useRef(0);


	const endOfMonth = (date) => {
		const year = date.getFullYear();
		const month = date.getMonth();

		return new Date(year, month + 1, 0);
	};

	const generateDatesRegular: () => DateRange[] = () => {
		const dates: DateRange[] = [];

		// Set up initial values
		const currentYear = new Date().getFullYear();
		const lastYear = currentYear - 1;

		// const cleanTimezone = (date) => {
		// 	const userTimezoneOffset = date.getTimezoneOffset() * 60000;
		// 	const idk = new Date(date.getTime() - userTimezoneOffset);
		// 	return idk;
		// };

		// Set up the date ranges
		for (let i = 1; i <= 12; i++) {
			const lastBegin = new Date(lastYear, i - 1, 1);

			const lastEnd = endOfMonth(lastBegin);
			dates.push({
				Begin: lastBegin, End: lastEnd, Data: null, MonthValue: i, YearValue: lastYear
			});

			const currentBegin = new Date(currentYear, i - 1, 1);
			const currentEnd = endOfMonth(currentBegin);
			dates.push({
				Begin: currentBegin, End: currentEnd, Data: null, MonthValue: i, YearValue: currentYear
			});
		}

		return dates;
	};

	const generateDatesSpringsStyle: () => DateRange[] = () => {
		const dates: DateRange[] = [];

		// Set up initial values
		const currentDate = new Date();
		let currentYear = currentDate.getFullYear();

		let lastYear = currentYear - 1;

		for (let i = 1; i <= 12; i++) {
			// Start day of month is always Sunday. If the first is on sunday - wednesday, then the sunday is the first of this month.
			// If the first is on thursday - saturday, then the next sunday is the first of this month.

			// Last day of the month is always Saturday. If the last day of a month is on a sunday - tuesday, then the past saturday is the last of this month.
			// If the last day of a month is on wednesday - saturday, then the next saturday is the last of this month.

			let begin: Date;
			let end: Date;

			const expectedMonth = i - 1; // 0-based month for Date constructor

			// Begin date
			const firstOfMonth = new Date(currentYear, expectedMonth, 1);
			const firstDay = firstOfMonth.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday

			// Find the first day correction count
			const firstDayCorrection = firstDay <= 3 ? 0 - firstDay : 7 - firstDay;
			begin = new Date(currentYear, expectedMonth, 1 + firstDayCorrection);

			// End date
			const lastOfMonth = endOfMonth(firstOfMonth);
			const lastDay = lastOfMonth.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday

			// Find the last day correction count
			const lastDayCorrection = lastDay <= 2 ? 0 - lastDay : 7 - lastDay;
			end = new Date(currentYear, expectedMonth, lastOfMonth.getDate() + lastDayCorrection - 1);

			dates.push({
				Begin: begin, End: end, Data: null, MonthValue: i, YearValue: currentYear
			});

			// Now do last year
			const lastFirstOfMonth = new Date(lastYear, expectedMonth, 1);
			const lastFirstDay = lastFirstOfMonth.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday	

			// Find the first day correction count
			const lastFirstDayCorrection = lastFirstDay <= 3 ? 0 - lastFirstDay : 7 - lastFirstDay;
			begin = new Date(lastYear, expectedMonth, 1 + lastFirstDayCorrection);

			// End date
			const lastLastOfMonth = endOfMonth(lastFirstOfMonth);
			const lastLastDay = lastLastOfMonth.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday

			// Find the last day correction count
			const lastLastDayCorrection = lastLastDay <= 2 ? 0 - lastLastDay : 7 - lastLastDay;
			end = new Date(lastYear, expectedMonth, lastLastOfMonth.getDate() + lastLastDayCorrection - 1);
			dates.push({
				Begin: begin, End: end, Data: null, MonthValue: i, YearValue: lastYear
			});
		}

		return dates;
	}

	const generateDates = isSpringsStyle ? generateDatesSpringsStyle : generateDatesRegular;


	const addDateRangeData = (month: number, year: number, data: VauluesExpected) => {
		const dates = generateDates();
		const dateRange = dates.find((date) => {
			return date.MonthValue === month && date.YearValue === year;
		}) ?? {
			Begin: new Date(), End: new Date(), Data: null, MonthValue: 0, YearValue: 0
		};

		if (dateRange) {
			dateRange.Data = data;
		}

		const datavals = dataValsRef.current;

		const newDataVals = datavals.dateRanges.map((date) => {
			if (date.MonthValue === month && date.YearValue === year) {
				return dateRange;
			}
			return date;
		});

		countRef.current += 1;

		dataValsRef.current = { dateRanges: newDataVals };


		if (countRef.current === 24) {
			setIsLoading(false);
		}

	};

	const dateConverter = (date, startOrEnd) => {
		// 2012-04-23T18:25:43.511Z
		const dd1 = String(date.getDate()).padStart(2, "0");
		const mm2 = String(date.getMonth() + 1).padStart(2, "0"); // January is 0!
		const yyyy3 = date.getFullYear();
		// T23:59:59
		return startOrEnd === "start" ? `${yyyy3}-${mm2}-${dd1}T00:00:00` : `${yyyy3}-${mm2}-${dd1}T23:59:59`;
	};

	const generateData = async (springsStyle: boolean) => {
		const dates = generateDates();


		// Shipped Ordered
		/* {
			"start_date": "2022-07-25T20:02:07.724Z",
			"end_date": "2022-07-25T20:02:07.724Z",
			"sales_person_id": 0,
			"date_location": "string",
			"power_type": "string"
			} */
		dates.forEach(async (dateRange) => {
			if (dateRange.Data !== null) return;
			dateRange.Data = "Loading";

			const goodsOrdered = await apiCalls.RunReport("sales_report", {
				start_date: dateConverter(dateRange.Begin, "start") ?? "",
				end_date: dateConverter(dateRange.End, "end") ?? "",
				sales_person_id: 0,
				date_location: "Ordered"
			});

			const goodsShipped = await apiCalls.RunReport("sales_report", {
				start_date: dateConverter(dateRange.Begin, "start") ?? "",
				end_date: dateConverter(dateRange.End, "end") ?? "",
				sales_person_id: 0,
				date_location: "Shipped"
			});

			const orderedExtras = goodsOrdered?.data?.extras ?? {};
			const shippedExtras = goodsShipped?.data?.extras ?? {};

			const data = {
				SalesOrderAmountOrdered: parseFloat(orderedExtras['Total Recorded Salesorder/Cost Amount'] ?? "0"),
				InvoiceOrdered: parseFloat(orderedExtras['Total Recorded Invoice Amount'] ?? "0"),
				ProjectedCostOrdered: parseFloat(orderedExtras['Total Projected Salesorder/Cost Amount'] ?? "0"),
				ProjectedInvoiceOrdered: parseFloat(orderedExtras['Total Projected Invoice Amount'] ?? "0"),
				SalesOrderAmountShipped: parseFloat(shippedExtras['Total Recorded Salesorder/Cost Amount'] ?? "0"),
				InvoiceShipped: parseFloat(shippedExtras['Total Recorded Invoice Amount'] ?? "0"),
				ProjectedCostShipped: parseFloat(shippedExtras['Total Projected Salesorder/Cost Amount'] ?? "0"),
				ProjectedInvoiceShipped: parseFloat(shippedExtras['Total Projected Invoice Amount'] ?? "0"),
				ShadesShipped: parseFloat(shippedExtras['Number of Shades'] ?? "0"),
				ShadesOrdered: parseFloat(orderedExtras['Number of Shades'] ?? "0")
			};

			addDateRangeData(dateRange.MonthValue, dateRange.YearValue, data);
		});
		setDataVals({ dateRanges: dates }, springsStyle);
	};

	const { dateRanges } = dataVals;

	const firstYear = new Date().getFullYear() - 1;
	const secondYear = firstYear + 1;

	const getDataByMonthYear = (month: number, year: number) => {
		const dateRange = dateRanges?.find((dr) => {
			const monthVal = dr.MonthValue;
			const yearVal = dr.YearValue;

			return monthVal === month && yearVal === year;
		});
		const returnVal = dateRange?.Data === "Loading" ? null : dateRange?.Data;

		return returnVal ?? {
			SalesOrderAmountOrdered: 0,
			InvoiceOrdered: 0,
			ProjectedCostOrdered: 0,
			ProjectedInvoiceOrdered: 0,
			SalesOrderAmountShipped: 0,
			InvoiceShipped: 0,
			ProjectedCostShipped: 0,
			ProjectedInvoiceShipped: 0,
			ShadesShipped: 0,
			ShadesOrdered: 0
		};
	};


	const rows: string[][][] = [];

	for (let year = firstYear; year <= secondYear; year++) {
		const row: string[][] = [];
		const yearRowItem: string[] = [];
		yearRowItem.push(`${year}`);
		row.push(yearRowItem);
		for (let month = 1; month <= 12; month++) {
			const dateRange = getDataByMonthYear(month, year);
			const dates = generateDates();
			const currentDateRange = dates.find((dr) => {
				const monthVal = dr.MonthValue;
				const yearVal = dr.YearValue;
				return monthVal === month && yearVal === year;
			}) ?? {
				Begin: new Date(), End: new Date(), Data: null, MonthValue: 0, YearValue: 0
			};

			const rowItem: string[] = [];

			rowItem.push(`Date Range: ${currentDateRange.Begin.toLocaleDateString()} - ${currentDateRange.End.toLocaleDateString()}`);

			rowItem.push(`Shades Ordered: ${dateRange.ShadesOrdered} `);
			rowItem.push(`Recorded Salesorder/Cost Amount Ordered: ${dateRange.SalesOrderAmountOrdered} `);
			rowItem.push(`Recorded Invoice Amount Ordered: ${dateRange.InvoiceOrdered} `);
			rowItem.push(`Projected Salesorder/Cost Amount Ordered: ${dateRange.ProjectedCostOrdered} `);
			rowItem.push(`Projected Invoice Amount Ordered: ${dateRange.ProjectedInvoiceOrdered} `);

			rowItem.push(`Shades Shipped: ${dateRange.ShadesShipped} `);
			rowItem.push(`Recorded Salesorder/Cost Amount Shipped: ${dateRange.SalesOrderAmountShipped} `);
			rowItem.push(`Recorded Invoice Amount Shipped: ${dateRange.InvoiceShipped} `);
			rowItem.push(`Projected Salesorder/Cost Amount Shipped: ${dateRange.ProjectedCostShipped} `);
			rowItem.push(`Projected Invoice Amount Shipped: ${dateRange.ProjectedInvoiceShipped} `);

			row.push(rowItem);
		}
		rows.push(row);
	}

	const percentDone = (100 * (countRef.current / 24)).toFixed(2);

	return (
		<Container className="local-bootstrap">
			<Card body>
				<h1><text>{`${isSpringsStyle ? "Springs" : "Base"} Top Level Report ${percentDone}%`}</text><button onClick={toggleStyle}>Swap Style</button></h1>
				<Table striped bordered responsive>
					<thead>
						<tr>
							<th colSpan={1}>Year</th>
							<th colSpan={1}>January</th>
							<th colSpan={1}>February</th>
							<th colSpan={1}>March</th>
							<th colSpan={1}>April</th>
							<th colSpan={1}>May</th>
							<th colSpan={1}>June</th>
							<th colSpan={1}>July</th>
							<th colSpan={1}>August</th>
							<th colSpan={1}>September</th>
							<th colSpan={1}>October</th>
							<th colSpan={1}>November</th>
							<th colSpan={1}>December</th>
						</tr>
					</thead>
					<tbody>
						{rows.map((row, index1) => {
							const rowIndex = `row-${index1}`;

							return (
								<tr key={rowIndex}>
									{row.map((cell, index2) => {
										const cellIndex = `cell-${index1}-${index2}`;

										return (
											<td key={cellIndex}>
												<Container>
													{cell.map((item, index3) => {
														const itemIndex = `item-${index1}-${index2}-${index3}`;

														const [label = "", val] = item.split(":");
														const valNum = parseFloat(val ?? '0');
														const valFormatted = valNum.toFixed(2);

														const isMoney = !label.includes("Shades") && !label.includes("Date Range");

														return (
															<Row key={itemIndex} className="mb-2">
																<Row className="text-nowrap mb-1">
																	<Col xs={12}>
																		{label}
																	</Col>
																</Row>
																<Row>
																	{val
																		&& (
																			<Col xs={12} className="text-muted">
																				{isMoney ? `$${valFormatted}` : val}
																			</Col>
																		)}
																</Row>
															</Row>
														)
													})}
												</Container>
											</td>
										);
									})}
								</tr>
							);
						})}
					</tbody>
				</Table>
			</Card>
		</Container>
	);
};

export default TopView;
